import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useReduxState } from 'hooks';
import api from 'api';
import { AxiosError } from 'axios';

export type ApiDataHookUpdateHandler = (
  params?: Partial<{ showLoading: boolean }>
) => Promise<void>;

interface Params<T> {
  allowUnauthenticatedUser?: boolean;
  errorMessage?: string;
  onDataUpdated?: (data: T) => void;
  onlyIf?: boolean;
  url: string;
}

const useApiData = <T = any>({
  allowUnauthenticatedUser = false,
  errorMessage,
  onDataUpdated,
  onlyIf,
  url
}: Params<T>) => {
  const { dispatch } = useDispatch();

  const { user } = useReduxState();

  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<Error | AxiosError | null>(null);
  const [loading, setLoading] = useState(true);

  const updateData: ApiDataHookUpdateHandler = useCallback(
    async ({ showLoading = false } = {}) => {
      try {
        if (showLoading) setLoading(true);
        setError(null);

        const response = await api.get(url);

        setData(response.data);
        setLoading(false);
        if (onDataUpdated) onDataUpdated(response.data);
      } catch (err) {
        if (err instanceof Error || err instanceof AxiosError) {
          setError(err);
        }
        setLoading(false);
        await dispatch.handleErrorDispatch(err, errorMessage);
      }
    },
    [url]
  );

  useEffect(() => {
    if ((Boolean(user?.id) || allowUnauthenticatedUser) && onlyIf !== false) {
      updateData({ showLoading: true });
    } else setLoading(false);
  }, [onlyIf, updateData]);

  return { data, error, loading, setLoading, updateData };
};

export default useApiData;
